import { useRef, useState } from "react"
import _ from "lodash"

//dnd-kit components
import {
  rectIntersection,
  pointerWithin,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { snapCenterToCursor } from "@dnd-kit/modifiers"

//Helpers
import { LogDebug, LogError, LogInfo } from "../../../helpers/logger"

//Styles
import myStyles from "../../../styles/assessments/ManagementFormBuilder.module.css"

//FormBuilderComponents
import FormBuilderElementsContainer from "./FormBuilder/Elements/FormBuilderElementsContainer"
import FormBuilderProperties from "./FormBuilder/Properties/FormBuilderProperties"
import FormBuilderForm from "./FormBuilder/Form/FormBuilderForm"
import ElementModel from "./FormBuilder/Elements/ElementModel"
import { GetNewOrderEntry } from "./FormBuilder/helpers/elementHelpers"
import FormTitlePanel from "./FormBuilder/Form/FormTitlePanel"
import elementTypes from "./FormBuilder/Elements/elementTypes"
import {
  MoveOrderEntryToNewIndex,
  moveElementQuestionFromGroupToQuestionOrder,
  moveElementQuestionFromQuestionOrderToGroup,
} from "./FormBuilder/helpers/dndElementHelper"
import {
  dndQOMoveQuestionOutOfGroup,
  dndQOMoveQuestionToGroup,
} from "./FormBuilder/helpers/dndQuestionOrderHelper"
import QuestionOrderSmall from "./FormBuilder/Form/QuestionOrderSmall"
import QuestionSmall from "./FormBuilder/Form/QuestionSmall"
import {
  dndQMoveQuestionToQuestionGroup,
  dndQMoveQuestionToQuestionOrder,
} from "./FormBuilder/helpers/dndQuestionHelper"
import FormulaBuilder from "./FormBuilder/FormulaBuilder/FormulaBuilder"

export default function ManagementFormBuilder({
  disabled,
  questionOrders,
  onSetFieldValue,
}) {
  const useQuestionOrderCopy = _.cloneDeep(questionOrders)

  const [tempQuestionOrders, setTempQuestionOrders] = useState(undefined)
  const [previewMode, setPreviewMode] = useState(false)
  const [showProperties, setShowProperties] = useState(false)
  const [showFormulaBuilder, setShowFormulaBuilder] = useState(false)
  const [editOrderId, setEditOrderId] = useState(undefined)
  const [editQuestionId, setEditQuestionId] = useState(undefined)

  //DnD handlers
  const [dndActiveElement, setDndActiveElement] = useState(undefined)
  const [dndActiveQuestionOrder, setDndActiveQuestionOrder] =
    useState(undefined)
  const [dndActiveQuestion, setDndActiveQuestion] = useState(undefined)

  //Tracking variables for dragged objects
  const [trackingOrderId, setTrackingOrderId] = useState(undefined)
  const [trackingQuestionGroupOrderId, setTrackingQuestionGroupOrderId] =
    useState(undefined)
  const [trackingQuestionId, setTrackingQuestionId] = useState(undefined)
  const lastElementMoveKeyRef = useRef("")
  const lastDragMoveKeyRef = useRef("")
  const dragMoveInProgressRef = useRef(false)
  const suppressedDragMoveCountRef = useRef(0)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor),
  )

  let formLayoutClass = myStyles.mainLayout

  if (previewMode) {
    formLayoutClass = myStyles.mainLayoutPreviewMode
  } else if (showProperties) {
    formLayoutClass = myStyles.mainLayoutWithProperties
  }

  try {
    const activeQuestionOrders =
      (tempQuestionOrders || useQuestionOrderCopy)
        ?.filter((order) => !order.isDeleted)
        .sort((a, b) => (a.orderNumber > b.orderNumber ? 1 : -1)) || []

    const getDragMoveType = () => {
      if (dndActiveElement) {
        return "element"
      }

      if (dndActiveQuestionOrder) {
        return "questionOrder"
      }

      if (dndActiveQuestion) {
        return "question"
      }

      return "unknown"
    }

    const buildDragMoveKey = (event) => {
      const activeData = event.active?.data?.current
      const overData = event.over?.data?.current

      return [
        getDragMoveType(),
        event.active?.id,
        event.over?.id,
        activeData?.element?.id || "",
        activeData?.questionOrder?.id || "",
        activeData?.question?.id || "",
        activeData?.questionGroup?.id || "",
        overData?.questionOrder?.id || "",
        overData?.question?.id || "",
        overData?.questionGroup?.id || "",
        trackingOrderId || "",
        trackingQuestionGroupOrderId || "",
        trackingQuestionId || "",
      ].join("|")
    }

    const shouldProcessDragMove = (event) => {
      // Prevent processing when a previous drag-move handler is still in-flight.
      if (dragMoveInProgressRef.current) {
        return false
      }

      const moveKey = buildDragMoveKey(event)

      if (lastDragMoveKeyRef.current === moveKey) {
        suppressedDragMoveCountRef.current += 1

        LogDebug(`Drag move suppressed (${suppressedDragMoveCountRef.current}) - same as last move key`, {
          moveKey,
          lastDragMoveKey: lastDragMoveKeyRef.current,
          event,
        })

        return false
      }

      lastDragMoveKeyRef.current = moveKey
      suppressedDragMoveCountRef.current = 0

      return true
    }

    const resetDragMove = (event) => {
      setTempQuestionOrders(undefined)
      setTrackingQuestionId(undefined)
      setTrackingQuestionGroupOrderId(undefined)

      lastDragMoveKeyRef.current = ""

      if (trackingOrderId !== event.active?.id) {
        setTrackingOrderId(undefined)
      }
    }

    //Note: Elements can cause the following events:
    //1. Dragged onto form:: Create new regular top level questionOrder entry at position, remove question from any group that it migth have been added to
    //2. Dragged onto an existing questionGroup (questionORder.questionGroup): Create new question in group, remove any top leven questionOrders linked to question
    const handleDraggingElement = (event) => {
      // Guard against repeated processing of the same drag frame,
      // which can create a state-update feedback loop and UI hang.
      const moveKey = [
        event.active?.id,
        event.over?.id,
        trackingOrderId,
        trackingQuestionGroupOrderId,
        trackingQuestionId,
      ].join("|")

      if (lastElementMoveKeyRef.current === moveKey) {
        return
      }

      lastElementMoveKeyRef.current = moveKey

      let activeNewId = trackingOrderId
      let newTempOrder = tempQuestionOrders || useQuestionOrderCopy

      if (
        event.over.data?.current?.questionGroup &&
        event.active.id !== elementTypes.groupElementId //Groups cannot nest, so ignore group handling
      ) {
        //This is a non-group type element hovering over a group, add it to the group
        //Remove it from the main questionOrders if it has been placed there

        const results = moveElementQuestionFromQuestionOrderToGroup(
          newTempOrder,
          trackingOrderId || trackingQuestionGroupOrderId,
          event.over.data?.current?.questionGroup.id,
          trackingQuestionId,
          event.over.data?.current?.question?.id,
          event.active.id,
        )

        newTempOrder = results.questionOrders
        setTempQuestionOrders(results.questionOrders)
        setTrackingOrderId(undefined)
        setTrackingQuestionGroupOrderId(results.questionOrderId)
        setTrackingQuestionId(results.questionId)
      } else {
        //Ignore if the object is being dragged over itself.
        if (event.over?.id !== trackingOrderId) {
          //This is should be set as a regular questionOrder
          //Remove it from any groups that it might belong to
          if (trackingQuestionGroupOrderId) {
            //This is a question that was formerly added to a question group
            //remove it from the group and move it to the main questionOrder list

            const results = moveElementQuestionFromGroupToQuestionOrder(
              newTempOrder,
              trackingQuestionGroupOrderId,
              trackingQuestionId,
            )

            setTempQuestionOrders(results.questionOrders)
            setTrackingOrderId(results.newQuestionOrderId)
            setTrackingQuestionGroupOrderId(undefined)
            setTrackingQuestionId(undefined)
          } else {
            if (!activeNewId) {
              const newOrderEntry = GetNewOrderEntry(
                event.active.id,
                useQuestionOrderCopy.length + 1,
              )
              newTempOrder = [...newTempOrder, newOrderEntry]
              activeNewId = newOrderEntry.id
              setTempQuestionOrders(newTempOrder)
              setTrackingOrderId(newOrderEntry.id)
              setTrackingQuestionId(newOrderEntry.question?.id)
            }

            const movedOrder = MoveOrderEntryToNewIndex(
              newTempOrder,
              activeNewId,
              event.over.id,
            )

            if (movedOrder !== newTempOrder) {
              setTempQuestionOrders(movedOrder)
            }

            //Clear any group values
            setTrackingQuestionGroupOrderId(undefined)
            setTrackingQuestionId(undefined)
          }
        }
      }
    }

    const handleDraggingQuestionOrderEntry = (event) => {
      try {
        let newTempOrder = tempQuestionOrders || useQuestionOrderCopy

        if (
          event.over.data?.current?.questionGroup &&
          dndActiveQuestionOrder.question
        ) {
          //This main questionOrder of type question is being dragged into a question group
          //Move the question to the group and delete the questionOrder entry
          const results = dndQOMoveQuestionToGroup(
            newTempOrder,
            event.active.id,
            event.over.data?.current?.questionGroup.id,
            event.over.data?.current?.question?.id,
          )

          setTempQuestionOrders(results.questionOrders)
          setTrackingQuestionGroupOrderId(results.questionGroupOrderId)
        } else if (
          !event.over.data?.current?.questionGroup &&
          dndActiveQuestionOrder.question &&
          trackingQuestionGroupOrderId
        ) {
          //This main questionOrder of type question is being dragged out of a question group
          //remove the question from the group and reinstate the questionOrder entry
          const results = dndQOMoveQuestionOutOfGroup(
            newTempOrder,
            event.active.id,
            trackingQuestionGroupOrderId,
          )

          setTempQuestionOrders(results)
          setTrackingQuestionGroupOrderId(undefined)
        }
      } catch (error) {
        LogError("Unable to process question order drag event", error)
      }
    }

    const handleDraggingQuestion = (event) => {
      try {
        let newTempOrder = tempQuestionOrders || useQuestionOrderCopy

        LogDebug(`${Date.now()} : Handling question drag event`, {
          active: event.active,
          over: event.over,
          trackingQuestionGroupOrderId,
          trackingOrderId,
          dndActiveQuestion,
        })

        if (
          event.over.data?.current?.questionOrder ||
          !event.over.data?.current?.question
        ) {
          //The question is being dragged over a regular question order, move it into the main questionOrder list
          const results = dndQMoveQuestionToQuestionOrder(
            newTempOrder,
            dndActiveQuestion,
            trackingQuestionGroupOrderId || trackingOrderId,
            event.over.id,
          )

          if (results) {
            setTempQuestionOrders(results.updatedQuestionOrders)

            if (results.currentQuestionOrder.question) {
              setTrackingOrderId(results.currentQuestionOrder.id)
              setTrackingQuestionGroupOrderId(undefined)
            } else {
              setTrackingOrderId(undefined)
              setTrackingQuestionGroupOrderId(results.currentQuestionOrder.id)
            }
          }
        } else if (event.over.data?.current?.questionGroup) {
          //The question is being dragged over a questionGroup type questionOrder, move it into the groups question list
          const results = dndQMoveQuestionToQuestionGroup(
            newTempOrder,
            dndActiveQuestion,
            trackingQuestionGroupOrderId || trackingOrderId,
            event.over.data.current.questionGroup.id,
            event.over.data.current?.question?.id,
            event.delta.y < 0,
          )

          if (results) {
            setTempQuestionOrders(results.updatedQuestionOrders)

            setTrackingOrderId(undefined)
            setTrackingQuestionGroupOrderId(results.currentQuestionOrder.id)
          }
        } else {
          LogInfo("Do nothing", event)
          LogInfo(event)
        }
      } catch (error) {
        LogError("Unable to process question drag event", error)
      }
    }

    const resetAllDragStates = () => {
      setTempQuestionOrders(undefined)
      setTrackingOrderId(undefined)
      setTrackingQuestionId(undefined)
      setTrackingQuestionGroupOrderId(undefined)
      setDndActiveElement(undefined)
      setDndActiveQuestionOrder(undefined)
      lastElementMoveKeyRef.current = ""
      lastDragMoveKeyRef.current = ""
      dragMoveInProgressRef.current = false
      suppressedDragMoveCountRef.current = 0
    }

    const setEdit = (orderId, questionId) => {
      setEditOrderId(orderId)
      setEditQuestionId(questionId)
      setShowProperties(true)
    }

    const clearEdit = () => {
      setEditOrderId(undefined)
      setEditQuestionId(undefined)
      setShowProperties(false)
    }

    const removeConditionalLinks = (questionId, questionOrders) => {
      let newOrders = _.cloneDeep(questionOrders)

      newOrders = newOrders.map((order) => {
        if (order.questionGroup) {
          order.questionGroup.questions = order.questionGroup.questions.map(
            (question) => {
              if (question.conditionalSource === questionId) {
                question.conditionalSource = undefined
                question.conditionalType = undefined
                question.conditionalValue = undefined
                question.isConditional = false
              }

              return question
            },
          )
        } else if (order.question) {
          if (order.question.conditionalSource === questionId) {
            order.question.conditionalSource = undefined
            order.question.conditionalType = undefined
            order.question.conditionalValue = undefined
            order.question.isConditional = false
          }
        }

        return order
      })

      return newOrders
    }

    const deleteScoreCalculationsForObject = (
      questionOrder,
      questionOrders,
    ) => {
      let newOrders = _.cloneDeep(questionOrders)

      const questionId = questionOrder?.question?.id
      const questionGroupId = questionOrder?.questionGroup?.id

      newOrders.forEach((order) => {
        if (order.question?.scoreFormula) {
          deleteFromScoreFormula(
            order.question.scoreFormula,
            questionId,
            questionGroupId,
          )
        } else if (order.questionGroup?.scoreFormula) {
          deleteFromScoreFormula(
            order.questionGroup.scoreFormula,
            questionId,
            questionGroupId,
          )
        }
      })

      return newOrders
    }

    const deleteFromScoreFormula = (
      scoreFormulas,
      questionId,
      questionGroupId,
    ) => {
      const testValue = (formula) => {
        return (
          (questionId && formula?.question?.questionId === questionId) ||
          (questionGroupId &&
            formula?.question?.questionGroupId === questionGroupId)
        )
      }

      return scoreFormulas.map((formula) => {
        let newFormula = _.cloneDeep(formula)

        if (testValue(newFormula)) {
          newFormula.isDeleted = true
          newFormula.question.isDeleted = true
        } else if (newFormula.function) {
          newFormula.function.values = newFormula.function.values.map(
            (value) => {
              if (testValue(value.question)) {
                let newValue = _.cloneDeep(value)
                newValue.isDeleted = true
                newValue.question.isDeleted = true

                return newValue
              }

              return value
            },
          )
        } else if (newFormula.group) {
          newFormula.group.formulas = newFormula.group.formulas.map(
            (groupFormula) => {
              let newGroupFormula = _.cloneDeep(groupFormula)

              if (testValue(newGroupFormula.question)) {
                newGroupFormula.isDeleted = true
                newGroupFormula.question.isDeleted = true
              } else if (newGroupFormula.function) {
                newGroupFormula.function.values =
                  newGroupFormula.function.values.map((value) => {
                    if (testValue(value.question)) {
                      let newValue = _.cloneDeep(value)
                      newValue.isDeleted = true
                      newValue.question.isDeleted = true

                      return newValue
                    }

                    return value
                  })
              }

              return newGroupFormula
            },
          )
        }

        return newFormula
      })
    }

    return (
      <>
        {showFormulaBuilder ? (
          <FormulaBuilder
            questionOptions={useQuestionOrderCopy}
            formikObjectName={"scoreFormula"}
            onClose={() => {
              setShowFormulaBuilder(false)
            }}
          />
        ) : null}
        <DndContext
          sensors={disabled ? undefined : sensors}
          collisionDetection={pointerWithin}
          onDragStart={(event) => {
            if (disabled) {
              return null
            }

            lastElementMoveKeyRef.current = ""
            lastDragMoveKeyRef.current = ""
            dragMoveInProgressRef.current = false
            suppressedDragMoveCountRef.current = 0

            let newTrackingOrderId = undefined
            let newTrackingQuestionId = undefined
            let newTrackingQuestionGroupOrderId = undefined
            let activeDndElement = undefined
            let activeQuestionOrder = undefined
            let activeQuestion = undefined

            if (event.active.data?.current?.element) {
              //We are dragging an element to add to the form
              activeDndElement = event.active.data.current.element
            } else if (event.active.data?.current?.questionOrder) {
              //We are dragging a questionOrder object for sorting in the form
              activeQuestionOrder = event.active.data?.current?.questionOrder
              newTrackingOrderId = event.active.id
            } else if (
              event.active.data.current.questionGroup &&
              event.active.data.current.question
            ) {
              //We are dragging a question from a group for sorting, or draggin out of the group
              const groupOrderEntry = useQuestionOrderCopy.find(
                (entry) =>
                  entry.questionGroup?.id ===
                  event.active.data.current.questionGroup.id,
              )

              activeQuestion = event.active.data.current.question
              newTrackingQuestionId = event.active.data.current.question.id
              newTrackingQuestionGroupOrderId = groupOrderEntry?.id
            }

            setDndActiveElement(activeDndElement)
            setDndActiveQuestionOrder(activeQuestionOrder)
            setDndActiveQuestion(activeQuestion)
            setTrackingOrderId(newTrackingOrderId)
            setTrackingQuestionGroupOrderId(newTrackingQuestionGroupOrderId)
            setTrackingQuestionId(newTrackingQuestionId)
          }}
          onDragMove={(event) => {
            if (disabled) {
              LogDebug("Drag move ignored - form is disabled", event)
              return null
            }

            if (!shouldProcessDragMove(event)) {
              return null
            }

            dragMoveInProgressRef.current = true

            try {
              //If the dragged item is an element, create a new orderEntry and put it in the array
              if (event.over && event.active) {
                if (dndActiveElement) {
                  LogDebug("Handling element drag move", event)
                  handleDraggingElement(event)
                } else if (dndActiveQuestionOrder) {
                  LogDebug("Handling questionOrder drag move", event)
                  handleDraggingQuestionOrderEntry(event)
                } else if (dndActiveQuestion) {
                  LogDebug("Handling question drag move", event)
                  handleDraggingQuestion(event)
                } else {
                  LogDebug("Resetting drag move", event)
                  resetDragMove(event)
                }
              } else if (!dndActiveQuestionOrder && !dndActiveQuestion) {
                LogDebug("No valid target - resetting drag move", event)
                resetDragMove(event)
              } else {
                LogDebug("Drag move with no target, ignoring", event)
              }
            } finally {
              dragMoveInProgressRef.current = false
            }
          }}
          onDragEnd={(event) => {
            if (disabled) {
              return null
            }

            if (event.over && event.active) {
              if (
                !event.active.data?.current?.element &&
                !dndActiveQuestion &&
                !trackingQuestionGroupOrderId
              ) {
                //Note: if trackingQuestionGroupOrderId has a value, we potentially have questionOrders converted to questions in groups or vice versa,
                //      so we cannot simply reorder
                //Logic: If we are not dragging in a new element and we are not dragging a group object, we are simply reordering questionOrder entries
                let newOrderList = _.cloneDeep(useQuestionOrderCopy)

                if (
                  event.active.data.current.questionGroup &&
                  event.over.data.current.questionGroup &&
                  event.active.data.current.questionGroup?.id ===
                    event.over.data.current.questionGroup?.id &&
                  event.active.id !== event.over.id
                ) {
                  const groupIndex = newOrderList.findIndex(
                    (entry) =>
                      entry.questionGroup?.id ===
                      event.active.data.current.questionGroup?.id,
                  )

                  if (groupIndex >= 0) {
                    const newQuestions = MoveOrderEntryToNewIndex(
                      _.cloneDeep(
                        newOrderList[groupIndex].questionGroup.questions,
                      ),
                      event.active.id,
                      event.over.id,
                    )

                    newOrderList[groupIndex].questionGroup.questions =
                      newQuestions
                  }
                } else {
                  newOrderList = MoveOrderEntryToNewIndex(
                    [...useQuestionOrderCopy],
                    event.active.id,
                    event.over.id,
                  )
                }

                //Persist the changes
                onSetFieldValue("questions", newOrderList)
              } else if (tempQuestionOrders) {
                onSetFieldValue("questions", [...tempQuestionOrders])
              }

              if (dndActiveElement) {
                setEdit(
                  trackingOrderId || trackingQuestionGroupOrderId,
                  trackingQuestionId,
                )
              }
            }

            resetAllDragStates()
          }}
          onDragCancel={() => {
            resetAllDragStates()
          }}
        >
          <DragOverlay modifiers={dndActiveElement ? [] : [snapCenterToCursor]}>
            {dndActiveElement ? (
              <ElementModel element={dndActiveElement} />
            ) : null}
            {dndActiveQuestionOrder ? (
              <QuestionOrderSmall questionOrder={dndActiveQuestionOrder} />
            ) : null}
            {dndActiveQuestion ? (
              <QuestionSmall question={dndActiveQuestion} />
            ) : null}
          </DragOverlay>
          <div className={myStyles.container}>
            <div className={formLayoutClass}>
              {!previewMode ? (
                <div className={myStyles.mainLayoutElements}>
                  <FormBuilderElementsContainer
                    disabled={disabled}
                    questionOrders={activeQuestionOrders}
                    onOutlineSelect={(questionOrderId, questionId) => {
                      setEditOrderId(questionOrderId)
                      setEditQuestionId(questionId)
                    }}
                  />
                </div>
              ) : null}

              <div className={myStyles.mainLayoutForm}>
                <div className={myStyles.mainLayoutFormTitle}>
                  <FormTitlePanel
                    preview={previewMode}
                    onPreviewChange={(newValue) => {
                      setPreviewMode(newValue)
                    }}
                    onShowScoreCalculation={() => {
                      setShowFormulaBuilder(true)
                    }}
                    disabled={disabled}
                  />
                </div>
                <div className={myStyles.mainLayoutFormBody}>
                  <FormBuilderForm
                    questionOrders={activeQuestionOrders}
                    selectedOrderId={trackingOrderId}
                    editOrderId={editOrderId}
                    editQuestionId={editQuestionId}
                    previewMode={previewMode}
                    disabled={disabled}
                    onDeleteQuestionOrder={(questionOrderId) => {
                      let newOrders = [...useQuestionOrderCopy]

                      let orderIndex = newOrders.findIndex(
                        (order) => order.id === questionOrderId,
                      )

                      //Update the question order to delete
                      let deletedOrder = newOrders[orderIndex]
                      deletedOrder.isDeleted = true

                      //Update the linked question or question group as deleted too
                      if (deletedOrder.question) {
                        deletedOrder.question.isActive = false

                        newOrders = removeConditionalLinks(
                          deletedOrder.question.id,
                          newOrders,
                        )
                      } else {
                        deletedOrder.questionGroup.isDeleted = true
                        deletedOrder.questionGroup.isActive = false
                        deletedOrder.questionGroup.questions.forEach(
                          (groupQuestion) => {
                            groupQuestion.isActive = false
                            newOrders = removeConditionalLinks(
                              groupQuestion.id,
                              newOrders,
                            )
                          },
                        )
                      }

                      newOrders[orderIndex] = {
                        ...deletedOrder,
                        isDeleted: true,
                      }

                      //Remove any score calculations that reference this object
                      newOrders = deleteScoreCalculationsForObject(
                        deletedOrder,
                        newOrders,
                      )

                      onSetFieldValue("questions", newOrders)

                      if (editOrderId === questionOrderId) {
                        clearEdit()
                      }
                    }}
                    onEditQuestionOrder={(questionOrderId) => {
                      setEdit(questionOrderId)
                    }}
                    onDeleteQuestion={(questionOrderId, questionId) => {
                      let newOrders = [...useQuestionOrderCopy]

                      let order = newOrders.find(
                        (order) => order.id === questionOrderId,
                      )

                      let question = order.questionGroup.questions.find(
                        (question) => question.id === questionId,
                      )

                      question.isActive = false

                      onSetFieldValue("questions", newOrders)

                      if (
                        editQuestionId === questionId ||
                        editOrderId === questionOrderId
                      ) {
                        clearEdit()
                      }
                    }}
                    onEditQuestion={(questionOrderId, questionId) => {
                      setEdit(questionOrderId, questionId)
                    }}
                  />
                </div>
              </div>
              {showProperties && !previewMode ? (
                <div className={myStyles.mainLayoutProperties}>
                  <FormBuilderProperties
                    questionOrders={useQuestionOrderCopy}
                    questionOrderId={editOrderId}
                    questionId={editQuestionId}
                    onClose={() => {
                      clearEdit()
                    }}
                    disabled={disabled}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </DndContext>
      </>
    )
  } catch (error) {
    LogError(error)
    return <>Something went wrong</>
  }
}
